import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { PayoutDetailsEntity } from "@modules/financesPayout/entities/payoutEntity"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import dayjs from "dayjs"
import { useCallback, useImperativeHandle, useState } from "react"
import { Link } from "react-router-dom"
import { useTheme } from "styled-components"

export type PayoutDetailsHelpModalRef = {
  open: () => void
}

type Props = {
  payoutDetails: PayoutDetailsEntity
  ref: React.Ref<PayoutDetailsHelpModalRef>
}

export function PayoutDetailsHelpModal({ payoutDetails, ref }: Props) {
  const theme = useTheme()
  const language = useLanguage()
  const [showModal, setShowModal] = useState(false)
  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  return (
    <HUDialog visible={showModal} header={null} onHide={onHide} style={{ width: "510px" }}>
      {/* HEADER */}
      <div className="flex flex-column gap-2 mt-4">
        <HUText
          fontStyle="TL"
          color={theme.blackAtheleWhiteOwner}
          className="flex pt-4 justify-content-center align-content-center text-center"
        >
          {word("finances.payoutHelpModal.searchTransfer")}
        </HUText>
        <HUText
          fontStyle="BM"
          color={theme.blackAtheleWhiteOwner}
          className="flex justify-content-center align-content-center"
        >
          {word("finances.payoutHelpModal.content", { bankName: payoutDetails.bankName })}
        </HUText>
      </div>
      <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner} className="flex w-full mt-4">
        {payoutDetails.bankName}
      </HUText>
      <div className="flex flex-column mt-3 gap-3">
        <div className="flex justify-content-between">
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutHelpModal.accountNumber")}
            </HUText>
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
              {payoutDetails.getFormatedDigits()}
            </HUText>
          </div>
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutHelpModal.routingNumber")}
            </HUText>
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
              {payoutDetails.routingNumber ?? word("global.notAvailable")}
            </HUText>
          </div>
        </div>
        <div className="flex justify-content-between">
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutDetails.transferMade")}
            </HUText>
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
              {dayjs(payoutDetails.createdAt).format("ll")}
            </HUText>
          </div>
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutHelpModal.transferAmount")}
            </HUText>
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
              {formatCurrency(payoutDetails.amount, payoutDetails.currency, language)}
            </HUText>
          </div>
        </div>
        <div className="flex flex-column gap-1">
          <HUText fontStyle="BS" color={theme.neutralColor600}>
            {word("finances.payoutDetails.internalNote")}
          </HUText>
          <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner} className="capitalize">
            {payoutDetails.note?.toLowerCase() ?? "-"}
          </HUText>
        </div>
        <div className="flex flex-column gap-1">
          <HUText fontStyle="BS" color={theme.neutralColor600}>
            {word("finances.payoutDetails.transferTrackingId")}
          </HUText>
          <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
            {payoutDetails.transferTrackingId ?? "-"}
          </HUText>
        </div>
      </div>
      <div className="flex justify-content-between gap-4 mt-4">
        <HUText fontStyle="BM" color={theme.neutralColor700}>
          {word("finances.payoutHelpModal.contactText")}
          <Link
            to={word("global.support.zendesk")}
            className="underline"
            style={{ color: theme.neutralColor700 }}
            target="_blank"
          >
            {word("finances.payoutHelpModal.contactUs")}
          </Link>
        </HUText>

        <HUButton
          text={word("finances.payoutHelpModal.closeModal")}
          onClick={() => {
            setShowModal(false)
          }}
          type="Right_Icon"
          size="S"
          colorType="Primary"
          className="flex px-3 py-1"
        />
      </div>
    </HUDialog>
  )
}
